import { FormEvent, ReactNode, createContext } from "react";

// TODO we should make our User and AuthError definition independent from Firebase
import { User, IdTokenResult, TotpSecret } from "./firebase-provider";
import { IncidentState } from "~/components/IncidentBanner/useIncidentBanner";

// types
export type AuthProviderProps = { children: ReactNode };

type AuthStateStatus =
  | "idle"
  | "pending"
  | "error"
  | "requires recent login"
  | "mfa required"
  | "pending mfa enrollment"
  | "pending secrets generation"
  | "mfa verifying";

// actions
type Action =
  | { type: "error"; error: string }
  | { type: "set user begin" }
  | {
      type: "set user success";
      user: User | null;
      idTokenResult: IdTokenResult | null;
    }
  | { type: "set user failure"; error: string }
  | { type: "reset password begin" }
  | { type: "reset password success" }
  | { type: "reset password failure" }
  | { type: "clear errors"; status?: AuthStateStatus }
  | { type: "set geolock"; isGeolocked: boolean }
  // MFA actions
  | { type: "set reauthentication begin"; status: AuthStateStatus }
  | { type: "requires recent login"; status: AuthStateStatus; error: string }
  | { type: "reauthentication success"; user: User; status: AuthStateStatus }
  | { type: "generate secrets begin"; status: AuthStateStatus }
  | { type: "enroll mfa secret generated" }
  | { type: "enroll mfa begin"; status: AuthStateStatus }
  | { type: "enroll mfa success"; user: User; status: AuthStateStatus }
  | { type: "enroll mfa failure"; error: string; status: AuthStateStatus }
  | { type: "mfa required"; status: AuthStateStatus }
  | { type: "mfa verification begin"; status: AuthStateStatus }
  | { type: "mfa verification success"; user?: User; status: AuthStateStatus }
  | { type: "mfa verification failure"; error: string; status: AuthStateStatus }
  | { type: "unenroll mfa begin"; status: AuthStateStatus }
  | { type: "unenroll mfa code required"; status: AuthStateStatus }
  | { type: "unenroll mfa verifying"; status: AuthStateStatus }
  | {
      type: "unenroll mfa success";
      status: AuthStateStatus;
      user: User;
    }
  | { type: "unenroll mfa failure"; status: AuthStateStatus; error: string };

export type Dispatch = (action: Action) => void;

export type State = {
  status: AuthStateStatus;
  user: User | null;
  idTokenResult: IdTokenResult | null;
  error: string | null;
  isGeolocked: boolean;
};

export type SignupProps = {
  email: string;
  password: string;
  optIn: boolean;
};

export type AuthOptions = "google" | "github" | "msft";

export type AuthContextTypes = {
  state: State;
  dispatch: Dispatch;
  logout: () => void;
  loginWithProvider: (
    e: FormEvent,
    dispatch: Dispatch,
    provideOption: AuthOptions,
  ) => void;
  signupWithEmailAndPassword: (
    e: FormEvent,
    dispatch: Dispatch,
    { email, password, optIn }: SignupProps,
  ) => void;
  loginWithEmailAndPassword: (
    e: FormEvent,
    dispatch: Dispatch,
    { email, password }: { email: string; password: string },
  ) => void;
  loginWithSSO: (e: FormEvent, dispatch: Dispatch, orgId: string) => void;
  sendResetPasswordEmail: (
    dispatch: Dispatch,
    email: string,
    e?: FormEvent,
  ) => void;
  sendUserEmailVerification: () => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  clearErrors: () => void;
  setGeolock: (isGeolocked: boolean) => void;
  handleErrorMessage: (errorCode: string) => string;
  // MFA functions
  reauthenticateForMfA: (params: { password?: string }) => Promise<void>;
  generateMfaSecrets: () => Promise<{
    qrCodeUrl: string;
    secret: TotpSecret;
  }>;
  enrollMfa: (params: {
    enrollmentSecret: TotpSecret;
    verificationCode: string;
  }) => Promise<void>;
  verifyMfaCodeForLogin: (params: {
    verificationCode: string;
  }) => Promise<void>;
  unenrollMfa: ({ password }: { password?: string }) => Promise<void>;
  verifyMfaCodeForUnenroll: ({
    verificationCode,
  }: {
    verificationCode: string;
  }) => Promise<void>;
  checkMFAStatus: (user: User | null) => boolean;
  incident: {
    data: IncidentState;
    showIncidentBanner: boolean;
    onCloseIncidentBanner: () => void;
  };
};

export const AuthContext = createContext<AuthContextTypes | undefined>(
  undefined,
);

export function authReducer(state: State, action: Action): State {
  switch (action.type) {
    case "error":
      return { ...state, error: action.error };
    case "set user begin":
      return { ...state, status: "pending" };
    case "set user success":
      return {
        ...state,
        status: "idle",
        user: action.user,
        idTokenResult: action.idTokenResult,
        error: null,
      };
    case "set user failure":
      return {
        ...state,
        status: "idle",
        user: null,
        idTokenResult: null,
        error: action.error,
      };
    case "reset password begin":
      return { ...state, status: "pending" };
    case "reset password success":
      return { ...state, status: "idle" };
    case "reset password failure":
      return { ...state, status: "idle" };
    case "clear errors":
      return { ...state, error: null, status: action.status || state.status };
    case "set geolock":
      return { ...state, isGeolocked: action.isGeolocked };
    // MFA actions
    case "requires recent login":
      return { ...state, status: action.status, error: action.error };
    case "set reauthentication begin":
      return { ...state, status: action.status };
    case "reauthentication success":
      return { ...state, status: "idle", user: action.user };
    case "enroll mfa begin":
      return { ...state, status: action.status };
    case "generate secrets begin":
      return { ...state, status: "pending" };
    case "enroll mfa secret generated":
      return { ...state, status: "idle" };
    case "enroll mfa success":
      return {
        ...state,
        status: action.status,
        user: action.user,
        error: null,
      };
    case "enroll mfa failure":
      return { ...state, status: action.status, error: action.error };
    case "mfa required":
      return { ...state, status: "mfa required" };
    case "mfa verification begin":
      return { ...state, status: action.status };
    case "mfa verification success":
      return { ...state, user: action.user || state.user, status: "idle" };
    case "mfa verification failure":
      return { ...state, error: action.error, status: action.status };
    case "unenroll mfa begin":
      return { ...state, status: "pending" };
    case "unenroll mfa code required":
      return { ...state, status: "mfa required" };
    case "unenroll mfa verifying":
      return { ...state, status: "mfa verifying" };
    case "unenroll mfa success":
      return {
        ...state,
        status: "idle",
        error: null,
        user: action.user || state.user,
      };
    case "unenroll mfa failure":
      return { ...state, status: "idle", error: action.error };
    default:
      throw new Error(`Unhandled action type: ${action}`);
  }
}

export const initialState: State = {
  status: "idle",
  user: null,
  idTokenResult: null,
  error: null,
  isGeolocked: false,
};
